'use client'

import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '../../../lib/utils'

import { TopNavigation, type TopNavigationProps } from '../TopNavigation'
import { DialogView, type DialogProps } from '../Dialog'
import { toast } from 'sonner'
import {
  ChatWindow,
  type ChatWindowProps,
} from '../ShadcnChat/components/ChatWindow'
import { Toast } from '../Toast'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import {
  SideNavigationComponent,
  SideNavigationV2,
  SideNavigationV2Props,
} from '../SideNavigationV2'
import { useTopNavigation } from '../TopNavigation/context'
import { ScrollProvider } from './scroll-context'
import { LEFT_SIDE_NAV_WIDTH, LEFT_SIDE_NAV_WIDTH_PX } from './constants'

const layoutVariants = cva('')

export interface LayoutProps extends VariantProps<typeof layoutVariants> {
  sideNavigation?: {
    left?: SideNavigationV2Props<SideNavigationComponent>
    right?: SideNavigationV2Props<SideNavigationComponent> & {
      width?: string
    }
  }
  topNavigation?: TopNavigationProps
  dialog?: DialogProps
  chatWindow?: ChatWindowProps
  children: React.ReactNode
  toastMessage?: {
    message: string
    timestamp: string
  }
  className?: string
  onRefresh?: () => void
  lockPageScroll?: boolean
}

const LayoutContent = React.forwardRef<HTMLDivElement, LayoutProps>(
  (
    {
      topNavigation,
      sideNavigation,
      dialog,
      toastMessage,
      children,
      chatWindow,
      className,
      onRefresh,
      lockPageScroll = false,
    },
    ref,
  ) => {
    const { topNavigationProps } = useTopNavigation()
    const [navVariant, setNavVariant] = React.useState<'slim' | 'default'>(
      'default',
    )

    const topNavContainerRef = React.useRef<HTMLDivElement>(null)
    const [topNavHeight, setTopNavHeight] = React.useState(0)

    const [viewPortIsMobile, setViewPortIsMobile] = React.useState(false)
    const [isRefreshing, setIsRefreshing] = React.useState(false)
    const touchStartY = React.useRef<number>(0)
    const scrollRef = React.useRef<HTMLDivElement>(
      null,
    ) as React.RefObject<HTMLDivElement>

    React.useEffect(() => {
      if (lockPageScroll && scrollRef.current) {
        scrollRef.current.scrollTop = 0
      }
    }, [lockPageScroll])

    const normalizeWidth = (
      width: string | undefined,
    ): { value: number; unit: string } => {
      if (!width) return { value: 250, unit: 'px' }

      const value = width.replace(/[^0-9.]/g, '')
      const unit = width.replace(/[0-9.]/g, '')

      return {
        value: parseFloat(value),
        unit: unit || 'px',
      }
    }

    const [viewportWidth, setViewportWidth] = React.useState(0)

    React.useEffect(() => {
      const handleResize = () => {
        if (window.innerWidth <= 768) {
          setViewPortIsMobile(true)
        } else {
          setViewPortIsMobile(false)
        }
        setViewportWidth(window.innerWidth)
      }
      handleResize()
      window.addEventListener('resize', handleResize)
      return () => {
        window.removeEventListener('resize', handleResize)
      }
    }, [])

    const handleScrollNavVariant = React.useCallback(() => {
      if (!scrollRef.current) return
      const top = scrollRef.current.scrollTop
      setNavVariant(top > 0 ? 'slim' : 'default')
    }, [])

    React.useEffect(() => {
      if ('scrollRestoration' in history) {
        history.scrollRestoration = 'manual'
      }
    }, [])

    const rightWidth = React.useMemo(() => {
      const { value, unit } = normalizeWidth(sideNavigation?.right?.width)
      if (unit === '%') {
        return Math.round((viewportWidth * value) / 100)
      }
      return Math.min(value, Math.round(viewportWidth * 0.8))
    }, [sideNavigation?.right?.width, viewportWidth])

    const leftWidth = LEFT_SIDE_NAV_WIDTH_PX

    const mergedTopNavigation = React.useMemo(() => {
      if (topNavigationProps === undefined && !topNavigation) return undefined

      const hasAllRequiredQueryBarProps = (qb: any) => {
        if (!qb || typeof qb.blockId !== 'string') return false
        const hasLegacyShape =
          qb.filters !== undefined &&
          typeof qb.onFiltersChange === 'function' &&
          qb.showableFields &&
          qb.queryFields
        const hasControlledShape =
          qb.state &&
          typeof qb.onStateChange === 'function' &&
          qb.showableFields &&
          qb.queryFields
        return hasLegacyShape || hasControlledShape
      }

      const sourceQueryBar = hasAllRequiredQueryBarProps(
        topNavigation?.queryBar,
      )
        ? topNavigation?.queryBar
        : hasAllRequiredQueryBarProps(topNavigationProps?.queryBar)
          ? topNavigationProps?.queryBar
          : undefined

      const queryBar = sourceQueryBar
        ? {
            blockId: sourceQueryBar!.blockId || '',
            state: sourceQueryBar!.state,
            onStateChange: sourceQueryBar!.onStateChange,
            sectionableFields: sourceQueryBar!.sectionableFields || [],
            showableFields: sourceQueryBar!.showableFields || [],
            queryFields: sourceQueryBar!.queryFields || [],
            relationshipPrefixes: sourceQueryBar!.relationshipPrefixes || [],
            fetchFieldsForPrefix: sourceQueryBar!.fetchFieldsForPrefix,
            className: sourceQueryBar!.className,
            pagination: sourceQueryBar!.pagination,
            modelName: sourceQueryBar!.modelName || '',
            sectionRelationConfig: sourceQueryBar!.sectionRelationConfig,
          }
        : undefined

      return {
        ...topNavigation,
        ...topNavigationProps,
        rightSlot: topNavigationProps?.rightSlot ?? topNavigation?.rightSlot,
        leftActions: [
          ...(topNavigation?.leftActions || []),
          ...(topNavigationProps?.leftActions || []),
        ],
        rightActions: [
          ...(topNavigation?.rightActions || []),
          ...(topNavigationProps?.rightActions || []),
        ],
        queryBar,
      }
    }, [topNavigation, topNavigationProps])

    React.useLayoutEffect(() => {
      const el = topNavContainerRef.current
      if (!el) {
        setTopNavHeight(0)
        return
      }

      const update = () => {
        const nextHeight = el.offsetHeight || 0
        setTopNavHeight((current) =>
          current === nextHeight ? current : nextHeight,
        )
      }
      update()

      const resizeObserver = new ResizeObserver(() => update())
      resizeObserver.observe(el)
      window.addEventListener('resize', update)

      return () => {
        resizeObserver.disconnect()
        window.removeEventListener('resize', update)
      }
    }, [mergedTopNavigation])

    const handleTouchStart = (e: React.TouchEvent) => {
      const firstTouch = e.touches[0]
      if (firstTouch) {
        touchStartY.current = firstTouch.clientY
      }
    }

    const handleTouchMove = React.useCallback(
      (e: React.TouchEvent) => {
        if (!scrollRef.current || isRefreshing || !onRefresh) return

        const firstTouch = e.touches[0]
        if (!firstTouch) return

        const touchY = firstTouch.clientY
        const touchDiff = touchY - touchStartY.current

        if (scrollRef.current.scrollTop <= 0 && touchDiff > 70) {
          setIsRefreshing(true)
          onRefresh()
          setTimeout(() => setIsRefreshing(false), 1000)
        }
      },
      [isRefreshing, onRefresh],
    )

    const prevToastTimestampRef = React.useRef<string | null>(null)

    React.useEffect(() => {
      if (
        toastMessage &&
        toastMessage.timestamp !== prevToastTimestampRef.current
      ) {
        prevToastTimestampRef.current = toastMessage.timestamp

        toast(
          <div>
            <div className="text-sm">{toastMessage.message}</div>
            <div className="text-xs text-gray-400">A few seconds ago</div>
          </div>,
          {
            unstyled: true,
          },
        )
      }
    }, [toastMessage])

    const handleScreenClick = () => {
      if (viewPortIsMobile && sideNavigation?.left?.isOpen) {
        sideNavigation.left.onSidebarOpenChange?.(false)
      }

      if (sideNavigation?.right?.isOpen) {
        sideNavigation.right.onSidebarOpenChange?.(false)
      }
    }

    return (
      <div ref={ref} className="w-screen h-[100dvh] overflow-hidden">
        <div
          className={cn(
            'flex overflow-hidden transition-transform duration-300 ease-in-out h-full',
            {
              relative: !viewPortIsMobile,
            },
          )}
          style={{
            width: viewPortIsMobile
              ? `calc(100% + ${String(rightWidth)}px + ${String(leftWidth)}px)`
              : `calc(100% + ${String(rightWidth)}px)`,
            marginLeft: viewPortIsMobile ? `-${String(leftWidth)}px` : '0',
            transform: viewPortIsMobile
              ? sideNavigation?.right?.isOpen
                ? `translateX(-${String(rightWidth)}px)`
                : sideNavigation?.left?.isOpen
                  ? `translateX(${String(leftWidth)}px)`
                  : 'translateX(0)'
              : sideNavigation?.right?.isOpen
                ? `translateX(-${String(rightWidth)}px)`
                : 'translateX(0)',
          }}
        >
          <div
            className="flex-shrink-0 bg-accent/20 relative"
            style={{ width: LEFT_SIDE_NAV_WIDTH }}
          >
            <div
              className="absolute inset-0 overflow-hidden"
              style={{ width: LEFT_SIDE_NAV_WIDTH }}
            >
              {sideNavigation?.left && (
                <SideNavigationV2 {...sideNavigation.left} />
              )}
            </div>
          </div>

          <div
            className={cn('relative flex-shrink-0', {
              'fixed inset-y-0': !viewPortIsMobile,
              'w-screen': viewPortIsMobile,
            })}
            style={{
              ...(!viewPortIsMobile && {
                left: LEFT_SIDE_NAV_WIDTH,
                width: `calc(100vw - ${leftWidth}px)`,
              }),
            }}
          >
            <div className="h-full flex flex-1">
              <div className="flex-1 bg-mobile-nav-background relative h-full flex flex-col">
                {mergedTopNavigation && (
                  <div
                    ref={topNavContainerRef}
                    className={cn('sticky top-0 z-10 w-full', {
                      'w-screen': viewPortIsMobile,
                    })}
                    style={
                      !viewPortIsMobile
                        ? {
                            width: `calc(100vw - ${leftWidth}px)`,
                          }
                        : undefined
                    }
                  >
                    <TopNavigation
                      {...mergedTopNavigation}
                      variant={navVariant}
                    />
                  </div>
                )}
                <div
                  ref={scrollRef}
                  className={cn(
                    'flex-1 max-w-screen h-full',
                    lockPageScroll
                      ? 'min-h-0 overflow-hidden'
                      : 'overflow-y-auto',
                    {
                      'h-[100dvh]': !viewPortIsMobile && !lockPageScroll,
                      'w-screen': viewPortIsMobile,
                    },
                    isRefreshing &&
                      'transition-transform duration-300 translate-y-16',
                  )}
                  style={{
                    ['--top-nav-height' as any]: `${topNavHeight}px`,
                    paddingTop: mergedTopNavigation ? topNavHeight : 0,
                    marginTop: mergedTopNavigation ? `-${topNavHeight}px` : 0,
                    WebkitOverflowScrolling: 'touch',
                    touchAction: 'pan-y',
                    ...(!viewPortIsMobile && {
                      width: `calc(100vw - ${leftWidth}px)`,
                    }),
                  }}
                  onClick={handleScreenClick}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                >
                  <ScrollProvider
                    scrollRef={scrollRef}
                    onScroll={handleScrollNavVariant}
                  >
                    {children}
                  </ScrollProvider>
                </div>

                {chatWindow && (
                  <div className="relative">
                    <ChatWindow {...chatWindow} />
                  </div>
                )}
              </div>
            </div>
          </div>

          {sideNavigation?.right && (
            <div
              className="flex-shrink-0 bg-accent/20 relative border-l"
              style={{
                width: `${rightWidth}px`,
                ...(!viewPortIsMobile && {
                  marginLeft: `calc(100vw - ${LEFT_SIDE_NAV_WIDTH})`,
                }),
              }}
            >
              <div
                className="absolute inset-0 overflow-hidden"
                style={{ width: `${rightWidth}px` }}
              >
                <div className="h-full">
                  <SideNavigationV2 {...sideNavigation.right} />
                </div>
              </div>
            </div>
          )}

          {dialog && dialog.open && <DialogView {...dialog} />}
        </div>
      </div>
    )
  },
)

const Layout = React.forwardRef<HTMLDivElement, LayoutProps>((props, ref) => {
  return (
    <DndProvider backend={HTML5Backend}>
      <LayoutContent {...props} ref={ref} />
      <Toast />
    </DndProvider>
  )
})

Layout.displayName = 'Layout'
LayoutContent.displayName = 'LayoutContent'

export { Layout, layoutVariants }
